'use client';

import { type KeyboardEvent, useId, useState } from 'react';

import './ProxyPlayground.css';
import '@/styles/gradient-surface.css';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { CodeBlockItem } from '@/components/atoms/CodeBlock/CodeBlockItem';
import CopyButton from '@/components/atoms/CodeBlock/CopyButton';

import { gradientDecodoBorderStyling } from '@/styles/gradient';

import type { ProxyPlaygroundCodeExample } from './types';

type ProxyPlaygroundCodePreviewProps = {
  codeExamples: ProxyPlaygroundCodeExample[];
  className?: string;
};

const LANGUAGE_TAB_CLASS = cn(
  'shrink-0 cursor-pointer rounded-full border px-3 py-1 font-serif text-body-s transition-colors',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400'
);

const LANGUAGE_TAB_IDLE_CLASS =
  'border-brand-500/22 bg-brand-500/22 text-gray-dark-400 hover:bg-brand-500/35';

const LANGUAGE_TAB_SELECTED_CLASS =
  'border-brand-500/70 bg-brand-500/70 text-gray-dark-25';

const ROOT_CLASS = cn(
  'brand-gradient-surface dark relative flex min-w-0 flex-col',
  gradientDecodoBorderStyling({
    borderWidth: 'p-px',
    borderRadius: 'rounded-2xl',
    color: 'gradient-secondary-1',
  }),
  'overflow-hidden before:hidden before:rounded-2xl before:z-10 lg:rounded-2xl lg:before:block',
  'max-lg:border-y max-lg:border-solid max-lg:border-gray-dark-600'
);

const CODE_AREA_CLASS = cn(
  'proxy-playground-scroll h-52 overflow-auto px-4 py-3 lg:px-5',
  '[&_pre]:whitespace-pre max-lg:[&_pre]:text-body-s max-lg:[&_pre]:leading-4'
);

export function ProxyPlaygroundCodePreview({
  codeExamples,
  className,
}: ProxyPlaygroundCodePreviewProps) {
  const { texts } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const panelId = useId();

  const activeExample = codeExamples[activeIndex] ?? codeExamples[0];
  if (!activeExample) return null;

  const showTabs = codeExamples.length > 1;

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex((index + 1) % codeExamples.length);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex((index - 1 + codeExamples.length) % codeExamples.length);
    }
  };

  return (
    <div
      className={cn(ROOT_CLASS, className)}
      data-tracker-component='proxy-playground-code-preview'
    >
      <div
        className='hidden border-t border-gray-dark-600 lg:block'
        aria-hidden='true'
      />

      <div className='relative'>
        <div
          id={panelId}
          role={showTabs ? 'tabpanel' : undefined}
          className={CODE_AREA_CLASS}
        >
          <CodeBlockItem
            key={activeExample.language}
            code={activeExample.code}
            language={activeExample.language}
          />
        </div>
        <div
          className='pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-linear-gradient(to top, rgba(15, 16, 18, 0.8), transparent)'
          aria-hidden='true'
        />
      </div>

      <div className='flex items-stretch justify-between border-t border-gray-dark-600 bg-gray-dark-900 lg:items-center lg:gap-4 lg:px-5 lg:py-4'>
        <div className='relative min-w-0 flex-1 border-r border-gray-dark-600 lg:border-r-0'>
          {showTabs ? (
            <div
              role='tablist'
              aria-label={texts.proxy_playground.language_tabs_label}
              className='proxy-playground-scroll flex gap-1.5 overflow-x-auto p-4 lg:flex-wrap lg:overflow-visible lg:p-0'
            >
              {codeExamples.map((example, index) => {
                const isSelected = index === activeIndex;

                return (
                  <button
                    key={example.language}
                    type='button'
                    role='tab'
                    aria-selected={isSelected}
                    aria-controls={panelId}
                    tabIndex={isSelected ? 0 : -1}
                    onClick={() => setActiveIndex(index)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                    className={cn(
                      LANGUAGE_TAB_CLASS,
                      isSelected
                        ? LANGUAGE_TAB_SELECTED_CLASS
                        : LANGUAGE_TAB_IDLE_CLASS
                    )}
                    data-tracker-component='proxy-playground-language-tab'
                  >
                    {example.language}
                  </button>
                );
              })}
            </div>
          ) : (
            <span className='block p-4 font-serif text-body-s text-gray-dark-400 lg:p-0'>
              {activeExample.language}
            </span>
          )}

          <div
            className='pointer-events-none absolute inset-y-0 right-0 w-14 bg-linear-to-l from-gray-dark-900 to-transparent lg:hidden'
            aria-hidden='true'
          />
        </div>

        <div className='flex shrink-0 items-center px-2 lg:px-0'>
          <CopyButton
            copyText={activeExample.code}
            ariaLabel={texts.proxy_playground.copy_code}
            classNames={{
              root: 'rounded p-2.5 hover:bg-gray-dark-800',
              copyIcon: 'h-5 w-5 text-base-dark-white',
              successIcon: 'h-5 w-5',
            }}
          />
        </div>
      </div>
    </div>
  );
}
